import React from "react";
import type { Message } from "../../../../redux/reducers/ChatReducer/ChatSliceTypes";

import type { Chat } from "../../../../redux/reducers/ChatReducer/ChatSliceTypes";
import type { User } from "../../../../redux/reducers/UserSlice/UserSliceType";
import { FilePreviews } from "../../types/messages.types";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import EmptyState from "./EmptyState";

interface ChatWindowProps {
  selectedChat: Chat | null;
  messages: Message[];
  currentUser: User | null;
  message: string;
  attachments: (string | File)[];
  filePreviews: FilePreviews;
  isUploading: boolean;
  messageLoading: boolean;
  page: number;
  totalPages: number;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemovePreview: (chatId: string, index: number) => void;
  onScroll: () => void;
  onBackToList: () => void;
  onToggleDetails: () => void;
  messageContainerRef: React.RefObject<HTMLDivElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isMobile?: boolean;
  showDetails?: boolean;
}

/**
 * ChatWindow Component
 * Center panel containing chat header, messages, and input
 */
const ChatWindow: React.FC<ChatWindowProps> = ({
  selectedChat,
  messages,
  currentUser,
  message,
  attachments,
  filePreviews,
  isUploading,
  messageLoading,
  page,
  onMessageChange,
  onSendMessage,
  onFileChange,
  onRemovePreview,
  onScroll,
  onBackToList,
  onToggleDetails,
  messageContainerRef,
  fileInputRef,
  setMessages,
  isMobile = false,
  showDetails = false,
}) => {
  // Prevent touch events from accidentally triggering back navigation
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  if (!selectedChat) {
    return (
      <div
        className={`flex-1 flex flex-col bg-neutral-50 dark:bg-neutral-900 ${
          showDetails && !isMobile ? "md:w-2/3 lg:w-1/2" : "md:w-2/3 lg:w-3/4"
        } ${!isMobile ? "hidden md:flex" : "hidden"}`}
      >
        <EmptyState type="no-selection" />
      </div>
    );
  }

  return (
    <div
      className={`flex-1 flex flex-col bg-neutral-50 dark:bg-neutral-900 ${
        showDetails && !isMobile ? "md:w-2/3 lg:w-1/2" : "md:w-2/3 lg:w-3/4"
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        touchAction: "none", // Prevent default touch behaviors
      }}
    >
      {/* Chat Header */}
      <ChatHeader
        selectedChat={selectedChat}
        onBack={onBackToList}
        onToggleDetails={onToggleDetails}
        showDetails={showDetails}
        isMobile={isMobile}
      />

      {/* Message List */}
      <MessageList
        messages={messages}
        messageLoading={messageLoading}
        page={page}
        currentUser={currentUser}
        selectedChat={selectedChat}
        messageContainerRef={messageContainerRef}
        onScroll={onScroll}
        setMessages={setMessages}
      />

      {/* Message Input */}
      <MessageInput
        message={message}
        attachments={attachments}
        filePreviews={filePreviews}
        selectedChatId={selectedChat.id.toString()}
        isUploading={isUploading}
        onMessageChange={onMessageChange}
        onSend={onSendMessage}
        onFileChange={onFileChange}
        onRemovePreview={onRemovePreview}
        fileInputRef={fileInputRef}
      />
    </div>
  );
};

export default ChatWindow;
